/**
 * Widok wynikow toola `WiseB2B_GET_api_ui_products` (scope AiSummaryProvider).
 *
 * Renderuje liste kart z nazwa, indeksem, cena i dostepnoscia. Klik prowadzi
 * do strony produktu (`/{lang}/products/{slug}`). Pokazujemy max `MAX_VISIBLE`
 * produktow — reszte za rozwijanym przyciskiem.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxSeam, CheckCircle, XCircle } from 'react-bootstrap-icons';

import styles from 'theme/components/containers/ChatBase/WiseAi/ProductsResultView.module.scss';

interface AiSummaryProductInfo {
  base_price_net?: number;
  base_price_gross?: number;
  price_net?: number;
  price_gross?: number;
  image?: string | null;
  stock_quantity?: number;
  name?: string;
  description?: string;
  index?: string;
  is_available?: boolean;
  url_link?: string;
  symbol?: string;
  unit_name?: string;
}

interface ProductItem {
  id: number;
  ai_summary_short_info?: AiSummaryProductInfo;
}

interface ProductsToolResponse {
  response?: {
    page?: number;
    total_count?: number;
    total_pages?: number;
    items?: ProductItem[];
  };
}

interface ProductsResultViewProps {
  payload: ProductsToolResponse | null;
}

const MAX_VISIBLE = 4;

const formatPrice = (value: number | undefined, currency = 'PLN'): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

export const ProductsResultView: React.FC<ProductsResultViewProps> = ({ payload }) => {
  const { t, i18n } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const items = payload?.response?.items ?? [];
  const totalCount = payload?.response?.total_count ?? items.length;

  if (items.length === 0) {
    return (
      <div className={styles.empty}>
        <BoxSeam size={20} />
        <span>{t('Nie znaleziono produktów spełniających kryteria.')}</span>
      </div>
    );
  }

  const visible = showAll ? items : items.slice(0, MAX_VISIBLE);
  const remaining = items.length - visible.length;
  const urlPrefix = `/${i18n.language}`;

  return (
    <div className={styles.wrap}>
      <div className={styles.headline}>
        {t('Znaleziono')} <strong>{totalCount}</strong> {t('produktów')}
      </div>

      <div className={styles.list}>
        {visible.map((item) => {
          const info = item.ai_summary_short_info ?? {};
          const href = info.url_link ? `${urlPrefix}/${info.url_link}` : undefined;
          const available = info.is_available === true && (info.stock_quantity ?? 0) > 0;

          return (
            <a
              key={item.id}
              className={styles.card}
              href={href}
              target="_blank"
              rel="noopener noreferrer">
              <div className={styles.imageBox}>
                {info.image ? (
                  <img src={info.image} alt={info.name ?? ''} loading="lazy" />
                ) : (
                  <BoxSeam size={28} />
                )}
              </div>
              <div className={styles.body}>
                <div className={styles.name} title={info.name}>{info.name}</div>
                {info.index && (
                  <div className={styles.index}>
                    {t('Indeks')}: <strong>{info.index}</strong>
                  </div>
                )}
                <div className={styles.priceRow}>
                  <span className={styles.priceNet}>{formatPrice(info.price_net)}</span>
                  <span className={styles.priceGross}>
                    {t('brutto')}: {formatPrice(info.price_gross)}
                  </span>
                </div>
                <div
                  className={styles.availability}
                  data-available={available ? 'true' : 'false'}>
                  {available ? <CheckCircle size={12} /> : <XCircle size={12} />}
                  <span>
                    {available
                      ? `${t('Dostępny')} (${info.stock_quantity} ${info.unit_name ?? t('szt.')})`
                      : t('Niedostępny')}
                  </span>
                </div>
              </div>
            </a>
          );
        })}
      </div>

      {remaining > 0 && (
        <button
          type="button"
          className={styles.moreBtn}
          onClick={() => setShowAll(true)}>
          {t('Pokaż pozostałe')} ({remaining})
        </button>
      )}
    </div>
  );
};
